import Papa from 'papaparse';
import React, { memo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box, { BoxProps } from 'ui-box';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ProductGroupTitle } from '../ProductGroupTitle';
import {
  Heading,
  majorScale,
  Spinner,
  Icon,
  Card,
  Select,
  Button,
  useToast,
} from '../../../npaper';
import { areEqual, FixedSizeList, ListChildComponentProps } from 'react-window';
import classNames from 'classnames';
import { ICompany, useStores } from '../../../stores';

export type ImportDataItemType = {
  sku: string;
  name?: string;
  description?: string;
  product?: number | string;
  retail_price?: number;
  manufacturer_number?: string;
  cost_price?: number;
  stock?: number;

  group?: number;
};

export type ImportDataType = {
  currency: string;
  supplier?: number;
  items: ImportDataItemType[];
};

const ImportCSVFile = ({
  onBack,
  onContinue,
  currency = 'EUR',
  supplier,
}: {
  onBack: () => void;
  onContinue: (data: any) => void;
  currency?: string;
  supplier?: ICompany;
}) => {
  const intl = useIntl();
  const toaster = useToast();

  const { productStore } = useStores();

  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState<Papa.ParseResult<any>>();
  const [parseError, setParseError] = useState<Papa.ParseError>();

  const [selectedFields, setSelectedFields] = useState<{
    [key: number]: string;
  }>({});

  const reset = () => {
    setParsing(false);
    setParseResult(undefined);
    setParseError(undefined);
    setSelectedFields({});
  };

  const fields = [
    {
      label: 'SKU',
      id: 'sku',
      format: (v: string) => v,
      decode: (v: string) => v,
    },
    {
      label: 'Name',
      id: 'name',
      format: (v: string) => v,
      decode: (v: string) => v,
    },
    {
      label: 'Description',
      id: 'description',
      format: (v: string) => v,
      decode: (v: string) => v,
    },
    {
      label: 'Manufacturer number',
      id: 'manufacturer_number',
      format: (v: string) => v,
      decode: (v: string) => v,
    },
    {
      label: intl.formatMessage({ defaultMessage: 'Product group' }),
      id: 'group',
      format: (v: string) =>
        isNaN(Number.parseInt(v)) ? (
          <p className="muted danger">
            <FormattedMessage
              defaultMessage="Not found: {groupId}"
              values={{ groupId: v }}
            />
          </p>
        ) : (
          <ProductGroupTitle productGroupId={Number(v)} />
        ),
      decode: (v: string) =>
        isNaN(Number.parseInt(v)) ? null : Number.parseInt(v),
    },
    {
      label: 'Retail price',
      id: 'retail_price',
      textAlign: 'right',
      format: (v: string) =>
        intl.formatNumber(Number.parseFloat(v.replace(',', '.')), {
          format: 'currency',
          currency: currency,
        }),
      decode: (v: string) => Number.parseFloat(v.replace(',', '.')),
    },
    {
      label: 'Cost price',
      id: 'cost_price',
      textAlign: 'right',
      format: (v: string) =>
        intl.formatNumber(Number.parseFloat(v.replace(',', '.')), {
          format: 'currency',
          currency: currency,
        }),
      decode: (v: string) => Number.parseFloat(v.replace(',', '.')),
    },
    {
      label: 'Stock',
      id: 'stock',
      textAlign: 'right',
      format: (v: string) =>
        intl.formatNumber(Number.parseFloat(v.replace(',', '.')), {
          format: 'unit',
        }),
      decode: (v: string) => Number.parseFloat(v.replace(',', '.')),
    },
  ];

  const makeImportPreview = async () => {
    if (!parseResult) {
      toaster.addToast({
        intent: 'warning',
        message: intl.formatMessage({
          defaultMessage: 'Please upload a CSV file to continue',
        }),
      });
      return;
    }
    if (!currency) {
      toaster.addToast({
        intent: 'warning',
        message: intl.formatMessage({
          defaultMessage: 'Please select a currency to continue',
        }),
      });
      return;
    }
    if (Object.values(selectedFields).indexOf('sku') === -1) {
      toaster.addToast({
        intent: 'warning',
        message: intl.formatMessage({
          defaultMessage: 'Please map the SKU field',
        }),
      });
      return;
    }
    if (Object.values(selectedFields).indexOf('name') === -1) {
      toaster.addToast({
        intent: 'warning',
        message: intl.formatMessage({
          defaultMessage: 'Please map the "Name" field',
        }),
      });
      return;
    }
    const items = parseResult.data.map((data: string[], i) => {
      const item: any = {};
      data.forEach((inner, j) => {
        if (!selectedFields[j]) return;
        const field = fields.find((f: any) => f.id === selectedFields[j]);
        if (!field) return;
        if (field.decode(inner)) item[selectedFields[j]] = field.decode(inner);
      });
      return item;
    });
    try {
      const data = await productStore.previewImport(items, currency, supplier);
      onContinue(data);
    } catch (err) {
      toaster.showDefaultError(err);
    }
  };

  return (
    <>
      {!parseResult && (
        <div className="py-8 w-full max-w-4xl mx-auto">
          <Heading size={300} marginBottom={majorScale(1)}>
            <FormattedMessage defaultMessage="Replacing products from supplier" />
          </Heading>
          <p className="mb-4">
            <FormattedMessage defaultMessage="When you import products from a supplier, any existing products from that supplier not in the import will be archived. If you have other suppliers for the product, it will not be archived." />
          </p>
          <Heading size={300} marginBottom={majorScale(1)}>
            <FormattedMessage
              defaultMessage="Upload CSV file"
              description="Heading for upload file"
            />
          </Heading>
          <p className="mb-4">
            <FormattedMessage defaultMessage="Upload a CSV file with the products to import." />
          </p>
          <DropZone
            paddingY={majorScale(8)}
            paddingX={majorScale(2)}
            display="flex"
            alignItems="center"
            justifyContent="center"
            onDropFiles={(files) => {
              setParsing(true);
              Papa.parse<any>(files[0], {
                delimiter: '',
                newline: '',
                worker: true,
                skipEmptyLines: true,
                header: false,

                complete: (result) => {
                  setParseResult(result);
                  setParsing(false);
                },
                error: (error) => {
                  setParseError(error);
                  setParsing(false);
                },
              });
            }}
          >
            <Box textAlign="center">
              {parsing ? (
                <Spinner />
              ) : (
                <>
                  <Icon icon="cloud-upload" size={40} />
                  <p className="muted">
                    <FormattedMessage defaultMessage="Upload or drag and drop file" />
                  </p>
                </>
              )}
            </Box>
          </DropZone>
          {parseError && (
            <div className="danger mt-4">
              <FormattedMessage defaultMessage="There was an error parsing the provided CSV file. Please ensure that the file is valid. For an example, try to open the file in Excel." />
            </div>
          )}

          <div className="mt-8 flex">
            <Button
              iconLeft="arrow-left"
              className="mr-2"
              onClick={() => onBack()}
            >
              <FormattedMessage defaultMessage="Back" />
            </Button>
          </div>
        </div>
      )}
      {parseResult && (
        <div className="py-8">
          <Box marginX="auto" maxWidth="800px" className="mb-8">
            <Heading size={300}>
              <FormattedMessage
                defaultMessage="Select fields"
                description="Heading for selecting fields"
              />
            </Heading>
            <Box is="p" className="mt-2" maxWidth="400px">
              <FormattedMessage
                defaultMessage="Next, you'll select the fields to map your data to. Use the header in the table, and select all the fields you need."
                description="Description for selecting fields"
              />
            </Box>
            <Box is="p" className="medium mt-2" maxWidth="400px">
              Required fields
            </Box>
            <Box is="p">SKU, Name</Box>
          </Box>

          <Card
            flexGrow={1}
            maxWidth="90vw"
            background="white"
            display="block"
            width={parseResult.data[0].length * 240}
            marginX="auto"
            height="440px"
          >
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fit, minmax(1px, 1fr));"
              paddingX={majorScale(2)}
              className="border-bottom"
              height={48}
            >
              {(parseResult.data[0] as string[]).map((_, i) => (
                <Box key={i} display="flex" alignItems="center">
                  <Select
                    items={fields}
                    itemRenderer={(item) => item.label}
                    hideSearchInput
                    onSelect={(item) => {
                      setSelectedFields((selectedFields) => {
                        const newFields = {
                          ...selectedFields,
                        };

                        if (newFields[i] === item.id) {
                          delete newFields[i];
                          return newFields;
                        }

                        Object.keys(newFields).forEach((key) => {
                          if (newFields[Number(key)] === item.id) {
                            delete newFields[Number(key)];
                          }
                        });

                        newFields[i] = item.id;
                        return newFields;
                      });
                    }}
                  >
                    <Button minimal iconRight="caret-down" small>
                      {selectedFields[i] &&
                      fields.find((f) => f.id === selectedFields[i]) ? (
                        fields.find((f) => f.id === selectedFields[i])!.label
                      ) : (
                        <span className="muted">Unmapped</span>
                      )}
                    </Button>
                  </Select>
                </Box>
              ))}
            </Box>
            <AutoSizer disableWidth>
              {({ height }) => (
                <FixedSizeList
                  height={height - 4 - 48}
                  width="100%"
                  itemSize={48}
                  itemCount={parseResult.data.length}
                  itemData={{
                    data: parseResult.data,
                    selectedFields,
                    fields,
                  }}
                >
                  {memo(
                    (props) => (
                      <RowRenderer {...props} />
                    ),
                    areEqual
                  )}
                </FixedSizeList>
              )}
            </AutoSizer>
          </Card>

          <Box marginX="auto" maxWidth="800px" paddingBottom={majorScale(4)}>
            <p className="small muted pt-2 pb-2">
              <FormattedMessage
                defaultMessage="Displaying {rows, plural, one {# row} other {# rows} }"
                values={{ rows: parseResult.data.length }}
              />
            </p>
            <Box display="flex" marginTop={majorScale(1)}>
              <Button
                onClick={() => {
                  reset();
                }}
                iconLeft="arrow-left"
              >
                <FormattedMessage defaultMessage="Back" />
              </Button>
              <Button
                primary
                className="ml-2"
                iconRight="arrow-right"
                onClick={async () => makeImportPreview()}
              >
                <FormattedMessage defaultMessage="Continue" />
              </Button>
            </Box>
          </Box>
        </div>
      )}
    </>
  );
};

const RowRenderer = (props: ListChildComponentProps) => {
  const { fields, selectedFields } = props.data;
  return (
    <Box
      style={props.style}
      display="grid"
      gridTemplateColumns="repeat(auto-fit, minmax(1px, 1fr));"
      paddingX={majorScale(2)}
      className="border-bottom"
      borderBottomWidth={1}
    >
      {(props.data.data[props.index] as string[]).map((s, i) => (
        <Box padding={majorScale(1)} key={i} display="flex" alignItems="center">
          <Box is="p" className={classNames('elipsis')}>
            {fields.find((f: any) => f.id === selectedFields[i])
              ? fields.find((f: any) => f.id === selectedFields[i])!.format(s)
              : s}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const DropZone: React.FC<
  BoxProps<'div'> & { onDropFiles: (files: FileList) => void }
> = ({ onDropFiles, children, ...props }) => {
  const [draggingOver, setDragginOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <Card
      cursor="pointer"
      onClick={() => {
        inputRef.current && inputRef.current.click();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragginOver(true);
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        setDragginOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragginOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragginOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
          onDropFiles(e.dataTransfer.files);
        }
      }}
      {...props}
      className={draggingOver ? 'bg-gray-50' : 'bg-white'}
    >
      <input
        type="file"
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (!e.target.files || e.target.files.length === 0) return;
          onDropFiles(e.target.files);
        }}
      />
      {children}
    </Card>
  );
};

export default ImportCSVFile;
